import { localeDirection } from '@/i18n';
import { useUiStore } from '@/stores/ui.store';

/** The active reading direction, derived from the UI store's locale (the
 * app-wide source of truth — see `ChromeSync`; instructions/04_UI_UX.md,
 * RTL-first). Charts use this to lay out their own coordinate space rather
 * than relying on a CSS mirror transform, which would also flip (and garble)
 * any SVG `<text>` labels. */
export function useChartDirection(): 'rtl' | 'ltr' {
  const locale = useUiStore((s) => s.locale);
  return localeDirection(locale);
}

/**
 * Ordinal x position for index `i` of `count` evenly-spaced points across
 * `[0, width]`. In LTR the first point (index 0 — the earliest bucket) sits at
 * the left; in RTL it sits at the right, so a time axis reads start → end in
 * the same direction as the surrounding text.
 */
export function ordinalX(index: number, count: number, width: number, dir: 'rtl' | 'ltr'): number {
  if (count <= 1) return dir === 'rtl' ? width : 0;
  const t = index / (count - 1);
  return dir === 'rtl' ? width - t * width : t * width;
}

/** Clamps a ratio to the finite [0, 1] range (guards against NaN/Infinity from
 * a divide-by-zero on an empty dataset). */
export function clampRatio(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}
